import { useCallback, useMemo, useRef, useState } from 'react'
import cn from 'classnames'
import { Link, useParams } from 'react-router-dom'
import { MdAccountTree, MdAssignmentTurnedIn, MdFileDownload, MdShowChart } from 'react-icons/md'
import {
    DOC_PROCESS_RUNSHEET_STATUSES,
    DOCUMENT_STATUS_SAVED,
    TITLE_CHAIN_CONVEYANCE_TYPES,
    LEASE_CHAIN_CONVEYANCE_TYPES,
} from '@/constants'
import RunsheetLegalDescriptionModal from '@/components/legaldescription/RunsheetLegalDescriptionModal'
import { Message } from '@/components/shared/Message'
import { DocumentViewerModal } from '@/components/collection/DocumentViewerV2'
import MergePartiesModal from './MergePartiesModal/MergePartiesModal'
import { UpsertDocument } from '../UpsertDocument'
import { FlowchartModal } from '@/components/ChainFlowchart'
import { useWireValue } from '@forminator/react-wire'
import * as store from '@/store'
import * as actions from '@/actions'
import { conveyanceRound, sumConveyance } from './ConveyanceUtils'
import ConveyanceDoc from './ConveyanceDoc'
import './ConveyanceTable.css'
import CircleSpinner from '@/components/shared/CircleSpinner'
import RunsheetTab from '../Runsheet/RunsheetTab'
import { getRunsheetDocsByTab } from '../Runsheet/Runsheet.lib'
import { toast } from 'react-toastify'

export const ConveyanceTable = ({
    chain,
    setChain,
    conveyanceType,
    runsheetDocs,
    activeTab,
    hasRecentFailure,
    hasMissingData,
    setHasRecentFailure,
    handleChain,
    exportChain,
    exportLoading,
    chainingBreadcrumb,
}) => {
    
    const { id: runsheetId } = useParams()
    
    const titleRef = useRef()
    const [showAdd, setShowAdd] = useState(false)
    const [editRsDocJoin, setEditRsDocJoin] = useState()
    const [showFlowchart, setShowFlowchart] = useState(false)
    const [selectedParty, setSelectedParty] = useState(null)
    
    const uniqueParties = useWireValue(store.uniqueParties)
    const tabs = useWireValue(store.runsheetTabs)
    
    const runsheet = useWireValue(store.currentRunsheet)
    const remoteConfig = useWireValue(store.remoteConfig)
    
    const chainHeaders = useMemo(() =>
        conveyanceType === 'title' ? TITLE_CHAIN_CONVEYANCE_TYPES : LEASE_CHAIN_CONVEYANCE_TYPES,
    [conveyanceType])
    
    const documents = useMemo(() => chain?.documents || [], [chain])
    
    const documentsToProcess = useMemo(() =>
        runsheetDocs
            .map(rdj => rdj.document)
            .filter(doc =>
                (doc.reviewStatus === DOCUMENT_STATUS_SAVED && !doc.orgStatus)
                || DOC_PROCESS_RUNSHEET_STATUSES.includes(doc.orgStatus),
            )
            .map(d => d.id) || [], [runsheetDocs])
    
    const parties = useMemo(() => {
        const partyData = documents.reduce((acc, curr) => {
            acc.push(
                ...curr.value.grantors.map(g => ({ ...g, partyType: 'grantor' })),
                ...curr.value.grantees.map(g => ({ ...g, partyType: 'grantee' })),
                ...(curr.value.grantorGrantee?.map(g => ({ ...g, partyType: 'grantor_grantee' })) || []),
            )
            return acc
        }, [])
        
        return Object.values(partyData.reduce((acc, curr) => {
            const party = acc[curr.id] = acc[curr.id] || {
                id: curr.id,
                name: curr.name,
                conveyances: [],
            }
            
            party.conveyances.push({ ...curr.conveyance, partyType: curr.partyType })
            return acc
        }, {}))
    }, [documents])
    
    const getTooltipContent = () => {
        if (documentsToProcess.length > 0)
            return 'All documents must be processed to use Auto-chain feature.'
        if (chain?.status === 'stale')
            return 'The runsheet has been updated. For improved results, please run Auto-chain again.'
        if (chain?.status === 'processing')
            return `${conveyanceType === 'title' ? 'Title' : 'Leasehold'} chain is processing. It should be ready soon.`
        return ''
    }
    
    const updateDocData = async () => {
        
        const data = await actions.fetchTitleBreakdown(runsheetId, activeTab)
        
        setChain(data)
        
    }
    
    const getTractLink = titleBreakdown =>
        `/tract/${titleBreakdown.tractId}/ownership?runsheetId=${titleBreakdown.runsheetId
        }&runsheetTabId=${titleBreakdown.runsheetTabId}`
    
    const getPartyMergeCount = useCallback(partyId =>
        uniqueParties?.find(up => up.id === partyId)?.parties.length || 1,
    [uniqueParties])
    
    const sumConveyances = (conveyances, field) => conveyanceRound(conveyances
        .reduce(sumConveyance(field), 0))
    
    const getTotalConveyances = field =>
        conveyanceRound(parties.reduce((acc, curr) => {
            
            return acc + curr.conveyances
                .reduce(sumConveyance(field), 0)
            
        }, 0))
    
    if (hasMissingData)
        return (
            <div className="flex flex-col">
                {chainingBreadcrumb()}
                <div className="flex flex-col items-center">
                    <div className="text-2xl my-5 mt-10">There's missing data from documents in this Runsheet.
                        The chainer can't proceed.</div>
                </div>
            </div>
        )
    
    if (!chain)
        return (
            <div className="flex flex-col">
                {chainingBreadcrumb()}
                <div className="flex flex-col items-center">
                    <div className="text-2xl my-5 mt-10">{conveyanceType === 'title'
                        ? 'Title Chain'
                        : 'Leasehold Chain'} is loading...</div>
                    <CircleSpinner />
                </div>
            </div>
        )
    
    
    return (
        <>
            {chainingBreadcrumb()}
            <div className="px-4 pb-4">
                {hasRecentFailure &&
                    <Message
                        message={{
                            text: 'Last Chaining failed. Try again to get latest results',
                            level: 'info',
                        }}
                        className="w-[500px]"
                        onDismiss={() => setHasRecentFailure(false)} />
                }
            </div>
            <div className="flex w-100 w-screen overflow-auto h-[calc(100vh-156px)]" ref={titleRef}>
                
                <div className="flex flex-col sticky left-0 title-chain-sidebar">
                    <table className={cn('bg-base-100 text-sm', {
                        'title-table': conveyanceType === 'title',
                        'leasehold-table': conveyanceType === 'leasehold',
                    })}>
                        <thead>
                            <tr className="sticky top-0">
                                <td className="!p-0 border-none" colSpan={chainHeaders.length + 1}>
                                    <div className="flex justify-between bg-base-200 sticky top-0 pt-2 ">
                                        <div className="flex flex-col gap-2 mx-4">
                                            <div
                                                data-tooltip-id="default-tip"
                                                data-tooltip-content={getTooltipContent()}>
                                                <button
                                                    className={cn('btn', {
                                                        'btn-neutral': chain?.status === 'stale',
                                                        'btn-primary': chain?.status !== 'stale',
                                                    })}
                                                    onClick={handleChain}
                                                    disabled={
                                                        chain?.status === 'processing'
                                                        || documentsToProcess.length > 0
                                                    }>
                                                    <MdAccountTree />Auto-Chain
                                                </button>
                                            </div>
                                            <button className="btn btn-primary" onClick={exportChain}
                                                disabled={exportLoading}>
                                                <MdFileDownload />Export
                                            </button>
                                            <button className="btn btn-primary" onClick={() => setShowFlowchart(true)}>
                                                <MdShowChart />Flowchart
                                            </button>
                                            {chain.tractId &&
                                                <Link className="btn btn-secondary"
                                                    to={getTractLink(chain)}>
                                                    <MdAssignmentTurnedIn />Ownership
                                                </Link>
                                            }
                                        </div>
                                        <div className="flex flex-col items-end gap-2 pr-2 pb-2 bg-base-200 text-sm">
                                            <span>Document Name</span>
                                            <span>Instrument Type</span>
                                            <span>Book Type / Volume / Page</span>
                                            <span>Instrument #</span>
                                            <span>File Date</span>
                                            <span>Effective / Instrument Date</span>
                                            <span>Grantors</span>
                                            <span>Grantees</span>
                                            <span>Comments</span>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                            <tr className="bg-base-200 sticky top-[260px]">
                                <td className="!p-0">
                                    <div className="border-b border-parse-800 pb-2 px-2">Sum Check</div>
                                </td>
                                <td colSpan={chainHeaders.length} className="!p-0">
                                    <div className="flex border-b border-parse-800 pb-2 px-2 gap-4">
                                        {chainHeaders.map(type => {
                                            const total = getTotalConveyances(type)
                                            const textColor = parseFloat(total) !== 1.0
                                                ? 'text-error' : 'text-slate-500'
                                            
                                            return (
                                                <div
                                                    key={`sumCheckTotal-${type}`}
                                                    className={`flex-1 text-center ${textColor}`}>
                                                    {total}
                                                </div>
                                            )
                                        })}
                                    </div>
                                </td>
                            </tr>
                            <tr className="bg-base-100 sticky top-[288px] ">
                                <th>Parties</th>
                                {chainHeaders.map(type => <th key={`sum-header-${type}`}>{type}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {parties.map((p, x) => {
                                const mergeCount = getPartyMergeCount(p.id)
                                
                                return <tr key={p.id}>
                                    <td
                                        data-x={x}
                                        className="bg-base-200">
                                        <div className="flex gap-2 cursor-pointer justify-end"
                                            onClick={() => setSelectedParty(p.id)}>
                                            <div
                                                className="truncate min-w-32 max-w-60"
                                                data-tooltip-id="defaut-tip"
                                                data-tooltip-content={p.name}>
                                                {p.name}
                                            </div>
                                            {mergeCount > 1 &&
                                                <div className="badge badge-primary w-5 p-0
                                            tooltip tooltip-right tooltip-primary"
                                                data-tip="The amount of parties merged with this name">
                                                    {mergeCount}
                                                </div>}
                                        </div>
                                    </td>
                                    {chainHeaders.map((type, y) => {
                                        const sum = sumConveyances(p.conveyances, type)
                                        
                                        return <td
                                            className={cn({ 'text-error': sum > 1 || sum < 0 })}
                                            data-conveyance-type={type}
                                            data-x={x}
                                            data-y={y}
                                            key={`sum-${type}`}>{sum}</td>
                                    })}
                                </tr>
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="flex">
                    {documents.map(doc =>
                        <ConveyanceDoc
                            key={doc.documentId}
                            conveyanceType={conveyanceType}
                            document={doc}
                            parties={parties}
                            rsDocJoin={runsheetDocs}
                            setEditRsDocJoin={setEditRsDocJoin}
                            showAdd={showAdd}
                            showHideAdd={setShowAdd} />)}
                </div>
            </div>
            
            <RunsheetTab runsheetId={runsheetId} canEdit={false} shouldSelectTab={tabId => {
                
                if (!remoteConfig.chainer?.validateMissingData) return true
                
                const tab = tabs.find(t => t.id === tabId)
                const docs = getRunsheetDocsByTab(tab, runsheet)
                // if important data is missing, then inform the user through a tooltip
                const hasMissingData = docs.some(r =>
                    !r.value.instrumentDate || !r.value.grantors.length || !r.value.grantees.length)
                
                if (hasMissingData)
                    toast.warn('There\'s missing data from documents in this Runsheet Tab. The chainer can\'t proceed.')
                
                return !hasMissingData
            }} />
            
            <DocumentViewerModal />
            
            {showAdd && (
                <UpsertDocument
                    runsheetId={runsheetId}
                    showAdd={showAdd}
                    setShowAdd={setShowAdd}
                    editRsDocJoin={editRsDocJoin}
                    onSuccess={updateDocData} />
            )}
            
            <RunsheetLegalDescriptionModal
                setShowAdd={setShowAdd}
                setEditRsDocJoin={setEditRsDocJoin} />
            
            <MergePartiesModal
                titleBreakdownId={chain.id}
                runsheetId={runsheetId}
                selectedParty={selectedParty}
                handleClose={async isSuccess => {
                    setSelectedParty(null)
                    
                    if (isSuccess) {
                        setChain(null)
                        const data = await actions.fetchTitleBreakdown(runsheetId, activeTab)
                        
                        setChain(data)
                        setHasRecentFailure(data.hasRecentFailure)
                        await actions.listUniqueParties(runsheetId)
                    }
                }
                } />
            
            <FlowchartModal
                open={showFlowchart}
                modalHandler={() => setShowFlowchart(false)}
                chain={chain} />
        </>
    )
} 